import { useMemo, useState } from "react";
import { ClockIcon, ShieldAlertIcon, Trash2Icon } from "lucide-react";

import { useCameras } from "@/api/cameras";
import { useEntities } from "@/api/entities";
import {
  useCreateModuleConfig,
  useCreateSchedule,
  useDeleteModuleConfig,
  useDeleteSchedule,
  useModuleConfigs,
  useSchedules,
  useUpdateModuleConfig,
  type ScheduleWindow,
} from "@/api/sinsight";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// A sensible BFSI default: closed 18:00–06:00, Mon–Fri.
const DEFAULT_WINDOW: ScheduleWindow = {
  days: [0, 1, 2, 3, 4],
  start: "18:00",
  end: "06:00",
};

type Scope = { kind: "camera" | "entity"; id: string };

function ScopePicker({
  scope,
  onChange,
}: {
  scope: Scope | null;
  onChange: (s: Scope | null) => void;
}) {
  const { data: cameras } = useCameras({ limit: 200 });
  const { data: entities } = useEntities();
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="space-y-1">
        <Label>Entity (branch) — sets hours once for all its cameras</Label>
        <select
          className="h-9 w-full rounded border bg-background px-2 text-sm"
          value={scope?.kind === "entity" ? scope.id : ""}
          onChange={(e) =>
            onChange(e.target.value ? { kind: "entity", id: e.target.value } : null)
          }
        >
          <option value="">— select entity —</option>
          {(entities ?? []).map((en) => (
            <option key={en.id} value={en.id}>{en.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label>Camera — overrides the entity schedule</Label>
        <select
          className="h-9 w-full rounded border bg-background px-2 text-sm"
          value={scope?.kind === "camera" ? scope.id : ""}
          onChange={(e) =>
            onChange(e.target.value ? { kind: "camera", id: e.target.value } : null)
          }
        >
          <option value="">— select camera —</option>
          {(cameras?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SchedulesCard() {
  const { data } = useSchedules();
  const create = useCreateSchedule();
  const del = useDeleteSchedule();

  const [scope, setScope] = useState<Scope | null>(null);
  const [name, setName] = useState("Closed hours");
  const [tz, setTz] = useState("Asia/Kolkata");
  const [windows, setWindows] = useState<ScheduleWindow[]>([{ ...DEFAULT_WINDOW }]);

  const canSave = Boolean(scope) && windows.length > 0;

  const onSave = async () => {
    if (!scope) return;
    await create.mutateAsync({
      name,
      timezone: tz,
      windows,
      camera_id: scope.kind === "camera" ? scope.id : undefined,
      entity_id: scope.kind === "entity" ? scope.id : undefined,
    });
    setWindows([{ ...DEFAULT_WINDOW }]);
  };

  const toggleDay = (wi: number, day: number) => {
    setWindows((ws) =>
      ws.map((w, i) => {
        if (i !== wi) return w;
        const days = new Set(w.days ?? []);
        if (days.has(day)) days.delete(day);
        else days.add(day);
        return { ...w, days: Array.from(days).sort((a, b) => a - b) };
      }),
    );
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <ClockIcon className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Closed-hours schedules</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          A schedule defines when a branch is "closed". After-hours
          detection raises an intrusion only when a person is seen
          inside one of these windows. Set it on an entity to cover
          every camera in the branch; a camera-specific schedule
          overrides the inherited one. Overnight windows (start &gt; end,
          e.g. 18:00→06:00) are supported.
        </p>

        <ScopePicker scope={scope} onChange={setScope} />

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Schedule name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Timezone (IANA)</Label>
            <Input value={tz} onChange={(e) => setTz(e.target.value)} />
          </div>
        </div>

        <div className="space-y-3">
          {windows.map((w, wi) => (
            <div key={wi} className="rounded border p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label>Start</Label>
                  <Input
                    type="time" value={w.start}
                    onChange={(e) =>
                      setWindows((ws) =>
                        ws.map((x, i) => (i === wi ? { ...x, start: e.target.value } : x)))
                    }
                    className="w-32"
                  />
                </div>
                <div className="space-y-1">
                  <Label>End</Label>
                  <Input
                    type="time" value={w.end}
                    onChange={(e) =>
                      setWindows((ws) =>
                        ws.map((x, i) => (i === wi ? { ...x, end: e.target.value } : x)))
                    }
                    className="w-32"
                  />
                </div>
                {windows.length > 1 && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setWindows((ws) => ws.filter((_, i) => i !== wi))}
                  >Remove</Button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {DAYS.map((d, di) => {
                  const active = (w.days ?? []).includes(di);
                  return (
                    <button
                      key={d} type="button"
                      onClick={() => toggleDay(wi, di)}
                      className={
                        "rounded px-2 py-1 text-xs " +
                        (active
                          ? "bg-[#263238] text-white"
                          : "border text-muted-foreground")
                      }
                    >{d}</button>
                  );
                })}
              </div>
            </div>
          ))}
          <Button
            variant="outline" size="sm"
            onClick={() => setWindows((ws) => [...ws, { ...DEFAULT_WINDOW }])}
          >Add window</Button>
        </div>

        <Button onClick={onSave} disabled={!canSave || create.isPending}>
          Save schedule
        </Button>

        <div className="mt-4 border-t pt-3">
          <h3 className="mb-2 text-sm font-semibold">Existing schedules</h3>
          {(data?.items ?? []).length === 0 && (
            <p className="text-xs text-muted-foreground">None yet.</p>
          )}
          <ul className="space-y-2">
            {(data?.items ?? []).map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded border p-2 text-sm"
              >
                <div>
                  <span className="font-medium">{s.name}</span>{" "}
                  <Badge variant={s.is_active ? "online" : "secondary"}>
                    {s.is_active ? "active" : "inactive"}
                  </Badge>
                  <div className="text-xs text-muted-foreground">
                    {s.camera_id ? "Camera scope" : "Entity scope"} ·{" "}
                    {s.timezone} ·{" "}
                    {s.windows.map((w) => `${w.start}–${w.end}`).join(", ")}
                    {s.subject_id && (
                      <span className="ml-1 font-mono opacity-60">
                        · subj {s.subject_id.slice(0, 8)}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => del.mutate(s.id)}
                >
                  <Trash2Icon className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function ModuleConfigCard() {
  const { data } = useModuleConfigs({ module_key: "after_hours" });
  const create = useCreateModuleConfig();
  const update = useUpdateModuleConfig();
  const del = useDeleteModuleConfig();

  const [scope, setScope] = useState<Scope | null>(null);
  const [threshold, setThreshold] = useState(0.4);
  const [storeRaw, setStoreRaw] = useState(true);

  const canSave = Boolean(scope);

  const onSave = async () => {
    if (!scope) return;
    await create.mutateAsync({
      module_key: "after_hours",
      camera_id: scope.kind === "camera" ? scope.id : undefined,
      entity_id: scope.kind === "entity" ? scope.id : undefined,
      enabled: true,
      config: {
        person_confidence_threshold: threshold,
        store_raw_off_hours: storeRaw,
      },
    });
  };

  const rows = useMemo(() => data?.items ?? [], [data]);

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <ShieldAlertIcon className="h-5 w-5 text-[#ff6d00]" />
          <h2 className="text-lg font-semibold">After-hours detection</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Enable server-side person detection per camera or branch. When
          a person is detected during the closed-hours schedule, an
          <strong> after-hours intrusion</strong> alert + notification is
          raised. Confidence threshold tunes how sure the model must be
          before a person counts.
        </p>

        <ScopePicker scope={scope} onChange={setScope} />

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Person confidence threshold ({threshold.toFixed(2)})</Label>
            <input
              type="range" min={0} max={1} step={0.05} value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={storeRaw}
              onChange={(e) => setStoreRaw(e.target.checked)}
            />
            Store off-hours person detections (don't raise intrusion)
          </label>
        </div>

        <Button onClick={onSave} disabled={!canSave || create.isPending}>
          Enable after-hours detection
        </Button>

        <div className="mt-4 border-t pt-3">
          <h3 className="mb-2 text-sm font-semibold">Configured cameras / branches</h3>
          {rows.length === 0 && (
            <p className="text-xs text-muted-foreground">None yet.</p>
          )}
          <ul className="space-y-2">
            {rows.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between rounded border p-2 text-sm"
              >
                <div>
                  <span className="font-medium">
                    {c.camera_id ? "Camera scope" : "Entity scope"}
                  </span>{" "}
                  <Badge variant={c.enabled ? "online" : "secondary"}>
                    {c.enabled ? "enabled" : "disabled"}
                  </Badge>
                  <div className="text-xs text-muted-foreground">
                    threshold{" "}
                    {String(
                      (c.config as Record<string, unknown>)
                        .person_confidence_threshold ?? "—",
                    )}{" "}
                    · store-raw{" "}
                    {String(
                      (c.config as Record<string, unknown>).store_raw_off_hours
                        ?? true,
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() =>
                      update.mutate({ id: c.id, patch: { enabled: !c.enabled } })
                    }
                  >{c.enabled ? "Disable" : "Enable"}</Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => del.mutate(c.id)}
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function StatelessModulesCard() {
  const { data } = useModuleConfigs();
  const create = useCreateModuleConfig();
  const update = useUpdateModuleConfig();
  const del = useDeleteModuleConfig();

  const [scope, setScope] = useState<Scope | null>(null);
  const [crowdThreshold, setCrowdThreshold] = useState(5);
  const [blur, setBlur] = useState(100);
  const [luma, setLuma] = useState(15);

  const rows = useMemo(
    () =>
      (data?.items ?? []).filter((c) =>
        ["crowd_count", "camera_tamper"].includes(c.module_key),
      ),
    [data],
  );

  const enable = async (
    module_key: string,
    config: Record<string, unknown>,
  ) => {
    if (!scope) return;
    await create.mutateAsync({
      module_key,
      camera_id: scope.kind === "camera" ? scope.id : undefined,
      entity_id: scope.kind === "entity" ? scope.id : undefined,
      enabled: true,
      config,
    });
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <ShieldAlertIcon className="h-5 w-5 text-[#ff6d00]" />
          <h2 className="text-lg font-semibold">Crowd &amp; tamper</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          <strong>Crowd count</strong> raises an event when the number of
          people in frame meets the threshold. <strong>Camera tamper</strong>
          {" "}uses image statistics (blur / black-out) — it needs no people
          and no zones. Zone &amp; line shapes are drawn on the{" "}
          <span className="font-medium">Analytics · Zones</span> page.
        </p>

        <ScopePicker scope={scope} onChange={setScope} />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 rounded border p-3">
            <h3 className="text-sm font-semibold">Crowd count</h3>
            <Label>People threshold</Label>
            <Input type="number" min={1} value={crowdThreshold}
              onChange={(e) => setCrowdThreshold(Number(e.target.value))}
              className="w-32" />
            <Button size="sm" disabled={!scope || create.isPending}
              onClick={() => enable("crowd_count", { threshold: crowdThreshold })}>
              Enable crowd count
            </Button>
          </div>
          <div className="space-y-2 rounded border p-3">
            <h3 className="text-sm font-semibold">Camera tamper</h3>
            <div className="flex gap-2">
              <div>
                <Label>Blur thr.</Label>
                <Input type="number" value={blur}
                  onChange={(e) => setBlur(Number(e.target.value))} className="w-24" />
              </div>
              <div>
                <Label>Black thr.</Label>
                <Input type="number" value={luma}
                  onChange={(e) => setLuma(Number(e.target.value))} className="w-24" />
              </div>
            </div>
            <Button size="sm" disabled={!scope || create.isPending}
              onClick={() => enable("camera_tamper",
                { blur_threshold: blur, luma_threshold: luma })}>
              Enable camera tamper
            </Button>
          </div>
        </div>

        <div className="mt-2 border-t pt-3">
          <h3 className="mb-2 text-sm font-semibold">Configured</h3>
          {rows.length === 0 && (
            <p className="text-xs text-muted-foreground">None yet.</p>
          )}
          <ul className="space-y-2">
            {rows.map((c) => (
              <li key={c.id}
                className="flex items-center justify-between rounded border p-2 text-sm">
                <div>
                  <Badge variant="secondary">{c.module_key}</Badge>{" "}
                  <Badge variant={c.enabled ? "online" : "secondary"}>
                    {c.enabled ? "enabled" : "disabled"}
                  </Badge>
                  <span className="ml-1 text-xs text-muted-foreground">
                    {c.camera_id ? "camera" : "entity"} · {JSON.stringify(c.config)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => update.mutate({ id: c.id, patch: { enabled: !c.enabled } })}>
                    {c.enabled ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => del.mutate(c.id)}>
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsConfigPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Analytics — Module configuration
        </h1>
        <p className="text-sm text-muted-foreground">
          Configure closed-hours schedules + after-hours detection, and
          enable crowd-count / camera-tamper per camera or branch. Zone &amp;
          line shapes are drawn on the{" "}
          <span className="font-medium">Analytics · Zones</span> page.
          Detections land on the{" "}
          <span className="font-medium">Analytics · Detections</span> feed.
        </p>
      </div>
      <SchedulesCard />
      <ModuleConfigCard />
      <StatelessModulesCard />
    </div>
  );
}
